/**
 * Employee Master — Phase 1 of the Enterprise Workforce Suite.
 *
 * Owner/Admin-only page. Full CRUD for the workspace's employees with
 * auto-linking to existing non-Guardian users on create.
 *
 * Layout:
 *   * Header row: title + search + branch/status filters + "Add employee"
 *   * Data table: name, designation, department, branch, employment type,
 *     status. Row click → open detail drawer.
 *   * "Add employee" opens a tabbed dialog:
 *       - Link user (search box) OR fill-in name/email/phone
 *       - Employment: designation, department, branch, type, joined_on
 *       - Bank & Statutory: bank details, PAN/Aadhaar/PF/ESI
 *       - Emergency & Skills: contacts, skill tags
 *       - Certifications: with issued/expires dates
 *       - Employment history
 *
 * All writes hit /api/workforce/employees (403 gated on backend).
 */
import { useState, useEffect, useCallback, useMemo } from "react";
import api, { formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { useAuth } from "@/contexts/AuthContext";
import {
  Card, CardHeader, CardTitle, CardContent,
} from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from "@/components/ui/table";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from "@/components/ui/dialog";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import {
  IdentificationBadge, Plus, MagnifyingGlass, Link as LinkIcon,
  UserCircle, WarningCircle, Trash, FileCsv,
} from "@phosphor-icons/react";
import CompensationLineItems from "@/components/workforce/CompensationLineItems";
import DataPortabilityDialog from "@/components/workforce/DataPortabilityDialog";
import { Checkbox } from "@/components/ui/checkbox";

import { devWarn } from "@/lib/devLog";
const STATUS_OPTIONS = [
  { value: "active",     label: "Active",     badge: "bg-emerald-100 text-emerald-800 border-emerald-200" },
  { value: "inactive",   label: "Inactive",   badge: "bg-slate-100 text-slate-700 border-slate-200" },
  { value: "on_leave",   label: "On Leave",   badge: "bg-amber-100 text-amber-800 border-amber-200" },
  { value: "terminated", label: "Terminated", badge: "bg-rose-100 text-rose-800 border-rose-200" },
];

const EMPLOYMENT_TYPES = [
  { value: "full_time",  label: "Full-time" },
  { value: "part_time",  label: "Part-time" },
  { value: "consultant", label: "Consultant" },
  { value: "intern",     label: "Intern" },
  { value: "contractor", label: "Contractor" },
];

const EMPTY_FORM = {
  name: "", employee_number: "", email: "", phone: "",
  designation: "", department: "", branch_id: "",
  employment_type: "full_time", joined_on: "", status: "active",
  linked_user_id: null,
  bank_details: { account_holder: "", bank_name: "", account_number: "", ifsc: "", branch: "" },
  statutory: { pan: "", aadhaar: "", pf_uan: "", pf_number: "", esi_number: "" },
  emergency_contacts: [{ name: "", relation: "", phone: "", email: "" }],
  skills: [],
  certifications: [],
  employment_history: [],
  notes: "",
  // Phase 4-b — compensation lives on the Employee record.
  compensation_model: "monthly",
  compensation_lines: [],
  app_access_enabled: false,
  // iter48 — Workflow fields
  job_level_id: "",
  reports_to: "",
  is_owner: false,
  // iter-67 — Therapist gate on the Employee record.
  is_therapist: false,
  therapies: [],
};

export default function EmployeesPage() {
  const { branches } = useAuth();
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [q, setQ] = useState("");
  const [statusFilter, setStatusFilter] = useState("all");
  const [branchFilter, setBranchFilter] = useState("all");
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editing, setEditing] = useState(null); // employee record when editing, else null
  const [form, setForm] = useState(EMPTY_FORM);
  const [saving, setSaving] = useState(false);
  // Iter-46 — sort by employee_number as a first-class column.
  const [sortKey, setSortKey] = useState("employee_number");
  const [sortDir, setSortDir] = useState("asc");
  // iter48 — Job Levels + Reports To picker state
  const [jobLevels, setJobLevels] = useState([]);
  const [reassignmentFlags, setReassignmentFlags] = useState([]);
  // iter-67 — therapy types for the Employment tab's Therapies section.
  const [therapyTypes, setTherapyTypes] = useState([]);
  // iter51 — Data Portability
  const [portabilityOpen, setPortabilityOpen] = useState(false);

  useEffect(() => {
    (async () => {
      try {
        const [j, r, t] = await Promise.all([
          api.get("/workflow/job-levels"),
          api.get("/workforce/employees/reassignment-required"),
          api.get("/therapy-types"),
        ]);
        setJobLevels(j.data || []);
        setReassignmentFlags(r.data || []);
        setTherapyTypes(t.data || []);
      } catch (e) {
        // These are optional workspace-config endpoints. If they fail
        // (e.g., add-on disabled), we log but never block the page.
        devWarn("[EmployeesPage] failed to load job levels / reassignment flags:", e);
      }
    })();
  }, []);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = {};
      if (q.trim()) params.q = q.trim();
      if (statusFilter !== "all") params.status = statusFilter;
      if (branchFilter !== "all") params.branch_id = branchFilter;
      const { data } = await api.get("/workforce/employees", { params });
      setRows(data);
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setLoading(false); }
  }, [q, statusFilter, branchFilter]);
  useEffect(() => { load(); }, [load]);

  // iter-67 — Deep-link handler. When the URL carries ?highlight=<empId>
  // (used by the Attendance page's clickable staff names to jump into
  // the employee profile), scroll the target row into view and open
  // the Edit dialog automatically. Runs once per load result set.
  useEffect(() => {
    if (!rows.length) return;
    const params = new URLSearchParams(window.location.search);
    const targetId = params.get("highlight");
    if (!targetId) return;
    const emp = rows.find(r => r.id === targetId || r.user_id === targetId);
    if (!emp) return;
    openEdit(emp);
    // Remove the query param so refreshing the page doesn't re-open
    // the dialog on every load.
    const url = new URL(window.location.href);
    url.searchParams.delete("highlight");
    window.history.replaceState({}, "", url.toString());
    // Best-effort scroll into view.
    requestAnimationFrame(() => {
      const el = document.querySelector(`[data-testid="employee-row-${emp.id}"]`);
      if (el) el.scrollIntoView({ behavior: "smooth", block: "center" });
    });
  }, [rows]);

  // Client-side sort applied after fetch. Backend search covers name/
  // email/designation; we extend it to also match employee_number so
  // "EMP-001" typed in the search box locates the row.
  const sortedRows = useMemo(() => {
    const filtered = q.trim()
      ? rows.filter(r =>
          [(r.employee_number || ""), (r.name || ""), (r.email || ""), (r.designation || "")]
            .some(v => v.toLowerCase().includes(q.trim().toLowerCase())))
      : rows;
    const dir = sortDir === "asc" ? 1 : -1;
    return [...filtered].sort((a, b) => {
      const va = (a[sortKey] || "").toString().toLowerCase();
      const vb = (b[sortKey] || "").toString().toLowerCase();
      if (va === vb) return 0;
      // Empty/missing numbers sink to the bottom regardless of direction.
      if (!va) return 1;
      if (!vb) return -1;
      return va > vb ? dir : -dir;
    });
  }, [rows, q, sortKey, sortDir]);

  const toggleSort = (key) => {
    if (sortKey === key) {
      setSortDir(d => (d === "asc" ? "desc" : "asc"));
    } else {
      setSortKey(key);
      setSortDir("asc");
    }
  };

  const openCreate = async () => {
    setEditing(null);
    // Preload next auto-generated employee number (falls back to blank if
    // auto-generation is disabled or the endpoint fails).
    let autoNumber = "";
    let autoOn = true;
    try {
      const { data } = await api.get("/workforce/employees/next-number");
      autoOn = !!data.auto_generate;
      autoNumber = autoOn ? (data.employee_number || "") : "";
    } catch (e) {
      devWarn("[EmployeesPage] auto-number lookup failed; user can enter manually:", e);
    }
    setForm({ ...EMPTY_FORM, employee_number: autoNumber, __auto_generate: autoOn });
    setDialogOpen(true);
  };
  const openEdit = (emp) => {
    setEditing(emp);
    setForm({
      ...EMPTY_FORM,
      ...emp,
      employee_number: emp.employee_number || "",
      linked_user_id: emp.user_id || null,
      branch_id: emp.branch_id || "",
      bank_details: emp.bank_details || EMPTY_FORM.bank_details,
      statutory: emp.statutory || EMPTY_FORM.statutory,
      emergency_contacts: emp.emergency_contacts?.length ? emp.emergency_contacts : EMPTY_FORM.emergency_contacts,
      skills: emp.skills || [],
      certifications: emp.certifications || [],
      employment_history: emp.employment_history || [],
      job_level_id: emp.job_level_id || "",
      reports_to: emp.reports_to || "",
      is_owner: !!emp.is_owner,
      // iter-67 — hydrate the therapist gate + therapy multi-select.
      is_therapist: !!emp.is_therapist,
      therapies: emp.therapies || [],
    });
    setDialogOpen(true);
  };

  const regenerateNumber = async () => {
    try {
      const { data } = await api.get("/workforce/employees/next-number");
      setForm(f => ({ ...f, employee_number: data.employee_number || "" }));
      toast.success(`Suggested: ${data.employee_number}`);
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const update = (k, v) => setForm(f => ({ ...f, [k]: v }));
  const updateNested = (parent, k, v) => setForm(f => ({
    ...f, [parent]: { ...(f[parent] || {}), [k]: v },
  }));

  const save = async (opts = {}) => {
    if (!form.name?.trim() && !form.linked_user_id) {
      return toast.error("Employee name is required (or link an existing user)");
    }
    // Iter-46 — manual mode requires a number.
    if (!editing && form.__auto_generate === false && !(form.employee_number || "").trim()) {
      return toast.error("Employee number is required — auto-generation is disabled in Settings.");
    }
    setSaving(true);
    // Strip client-only markers before POSTing.
    const { linked_user_id, __auto_generate, ...payload } = form;
    // Also drop `_ui_key` from nested list rows — added client-side
    // by NestedListEditor for stable React keys.
    const _stripKeys = (arr) => (arr || []).map(({ _ui_key, ...rest }) => rest);
    payload.emergency_contacts  = _stripKeys(payload.emergency_contacts);
    payload.skills              = _stripKeys(payload.skills);
    payload.certifications      = _stripKeys(payload.certifications);
    payload.employment_history  = _stripKeys(payload.employment_history);
    payload.compensation_lines  = _stripKeys(payload.compensation_lines);
    try {
      if (editing) {
        // iter-67e — When Web App Access is being enabled in this
        // save, call create-login FIRST so the linked user_id is
        // present when the PUT lands. Otherwise the iter-67b
        // therapist gate on PUT would reject employees whose
        // `is_therapist=True` while `user_id=None`.
        if (opts.webFormPayload) {
          try {
            await api.post(
              `/workforce/employees/${editing.id}/create-login`,
              opts.webFormPayload,
            );
          } catch (e) {
            toast.error(formatApiError(e, "Could not create the user login."));
            setSaving(false);
            return;
          }
        }
        await api.put(`/workforce/employees/${editing.id}`, payload);
        toast.success(
          opts.webFormPayload
            ? `Employee updated. Web application access enabled for ${opts.webFormPayload.email}.`
            : "Employee updated",
        );
      } else {
        await api.post("/workforce/employees", payload);
        toast.success(`Employee added${linked_user_id ? " and linked to user" : ""}`);
      }
      setDialogOpen(false);
      await load();
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setSaving(false); }
  };

  const remove = async (emp) => {
    if (!window.confirm(`Terminate employee "${emp.name}"? Their record stays in the system for payroll and audit; they'll no longer appear in Active lists.`)) return;
    try {
      await api.delete(`/workforce/employees/${emp.id}`);
      toast.success("Employee terminated");
      await load();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const branchNameById = useMemo(() => {
    const m = {};
    (branches || []).forEach(b => { m[b.id] = b.name; });
    return m;
  }, [branches]);

  return (
    <div className="p-6 max-w-7xl mx-auto space-y-4" data-testid="employees-page">
      {reassignmentFlags.length > 0 && (
        <Card className="border-amber-300 bg-amber-50">
          <CardContent className="p-3 flex items-start gap-2 text-sm">
            <WarningCircle size={18} className="text-amber-700 shrink-0 mt-0.5" />
            <div className="flex-1">
              <div className="font-medium text-amber-900">
                {reassignmentFlags.length} employee{reassignmentFlags.length > 1 ? "s" : ""} need
                a new line manager
              </div>
              <div className="text-[11px] text-amber-800 mt-1" data-testid="reassignment-banner">
                {reassignmentFlags.slice(0, 6).map((f, i) => (
                  <span key={f.id}>
                    <b>{f.employee_number || ""}{f.employee_number ? " — " : ""}{f.name}</b>
                    {i < Math.min(reassignmentFlags.length, 6) - 1 ? "; " : ""}
                  </span>
                ))}
                {reassignmentFlags.length > 6 && ` and ${reassignmentFlags.length - 6} more`}
                . Their previous Reports To is no longer active.
              </div>
            </div>
          </CardContent>
        </Card>
      )}
      <div className="flex items-center gap-3 flex-wrap">
        <IdentificationBadge size={28} className="text-primary" weight="duotone" />
        <div>
          <h1 className="text-2xl font-display font-bold tracking-tight">Employee Master</h1>
          <p className="text-xs text-muted-foreground">
            Every staff member and consultant in this workspace. Parents and guardians live in the Students module.
          </p>
        </div>
        <div className="ml-auto flex items-center gap-2 flex-wrap">
          <div className="relative">
            <MagnifyingGlass size={14} className="absolute left-2.5 top-2.5 text-muted-foreground" />
            <Input
              placeholder="Search name, email, designation…"
              value={q} onChange={e => setQ(e.target.value)}
              className="pl-8 h-9 w-64"
              data-testid="employees-search"
            />
          </div>
          <Select value={statusFilter} onValueChange={setStatusFilter}>
            <SelectTrigger className="h-9 w-36" data-testid="employees-status-filter"><SelectValue placeholder="All status" /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All statuses</SelectItem>
              {STATUS_OPTIONS.map(s => (<SelectItem key={s.value} value={s.value}>{s.label}</SelectItem>))}
            </SelectContent>
          </Select>
          <Select value={branchFilter} onValueChange={setBranchFilter}>
            <SelectTrigger className="h-9 w-40" data-testid="employees-branch-filter"><SelectValue placeholder="All branches" /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All branches</SelectItem>
              {(branches || []).map(b => (<SelectItem key={b.id} value={b.id}>{b.name}</SelectItem>))}
            </SelectContent>
          </Select>
          <Button variant="outline" onClick={() => setPortabilityOpen(true)} className="gap-1.5" data-testid="employees-portability-btn">
            <FileCsv size={14} /> Data Portability
          </Button>
          <Button onClick={openCreate} className="gap-1.5" data-testid="employees-add-btn">
            <Plus size={14} weight="bold" /> Add employee
          </Button>
        </div>
      </div>

      <Card>
        <CardContent className="p-0">
          <div className="overflow-x-auto">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead
                    className="cursor-pointer select-none w-36"
                    onClick={() => toggleSort("employee_number")}
                    data-testid="empnum-column-header"
                  >
                    Employee&nbsp;# {sortKey === "employee_number" ? (sortDir === "asc" ? "↑" : "↓") : ""}
                  </TableHead>
                  <TableHead
                    className="cursor-pointer select-none"
                    onClick={() => toggleSort("name")}
                  >
                    Name {sortKey === "name" ? (sortDir === "asc" ? "↑" : "↓") : ""}
                  </TableHead>
                  <TableHead>Designation / Dept</TableHead>
                  <TableHead>Job Level</TableHead>
                  <TableHead>Branch</TableHead>
                  <TableHead>Type</TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead>Contact</TableHead>
                  <TableHead className="w-32">Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {loading && (
                  <TableRow><TableCell colSpan={9} className="text-center py-8 text-sm text-muted-foreground">Loading…</TableCell></TableRow>
                )}
                {!loading && sortedRows.length === 0 && (
                  <TableRow>
                    <TableCell colSpan={9} className="text-center py-12 text-sm text-muted-foreground">
                      No employees yet. Click <b>Add employee</b> to begin.
                    </TableCell>
                  </TableRow>
                )}
                {sortedRows.map(emp => {
                  const status = STATUS_OPTIONS.find(s => s.value === emp.status) || STATUS_OPTIONS[0];
                  const jl = emp.job_level_id ? jobLevels.find(l => l.id === emp.job_level_id) : null;
                  return (
                    <TableRow key={emp.id} data-testid={`employee-row-${emp.id}`}>
                      <TableCell>
                        {emp.employee_number
                          ? <span className="font-mono text-xs font-semibold" data-testid={`empnum-cell-${emp.id}`}>{emp.employee_number}</span>
                          : <span className="text-[10px] text-amber-700 bg-amber-50 border border-amber-200 rounded px-1.5 py-0.5" data-testid={`empnum-empty-${emp.id}`}>Unassigned</span>
                        }
                      </TableCell>
                      <TableCell>
                        <div className="flex items-center gap-2">
                          <div className="font-medium">{emp.name || "—"}</div>
                          {emp.user_id && <LinkIcon size={12} className="text-muted-foreground" title="Linked to user account" />}
                        </div>
                        {emp.email && <div className="text-[11px] text-muted-foreground">{emp.email}</div>}
                      </TableCell>
                      <TableCell>
                        <div>{emp.designation || "—"}</div>
                        {emp.department && <div className="text-[11px] text-muted-foreground">{emp.department}</div>}
                      </TableCell>
                      <TableCell>
                        {jl ? (
                          <Badge variant="outline" className="text-[10px]" data-testid={`joblevel-cell-${emp.id}`}>
                            {jl.name}
                          </Badge>
                        ) : (
                          <span className="text-[11px] text-muted-foreground">—</span>
                        )}
                      </TableCell>
                      <TableCell className="text-sm">{branchNameById[emp.branch_id] || "—"}</TableCell>
                      <TableCell className="text-sm">
                        {EMPLOYMENT_TYPES.find(t => t.value === emp.employment_type)?.label || emp.employment_type || "—"}
                      </TableCell>
                      <TableCell>
                        <Badge className={status.badge}>{status.label}</Badge>
                      </TableCell>
                      <TableCell className="text-xs">{emp.phone || "—"}</TableCell>
                      <TableCell>
                        <div className="flex gap-1">
                          <Button variant="ghost" size="sm" onClick={() => openEdit(emp)} data-testid={`employee-edit-${emp.id}`}>
                            Edit
                          </Button>
                          {emp.status !== "terminated" && (
                            <Button variant="ghost" size="sm" onClick={() => remove(emp)} className="text-rose-600 hover:text-rose-700" data-testid={`employee-terminate-${emp.id}`}>
                              <Trash size={13} />
                            </Button>
                          )}
                        </div>
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </div>
        </CardContent>
      </Card>

      <EmployeeDialog
        open={dialogOpen}
        editing={editing}
        form={form}
        update={update}
        updateNested={updateNested}
        setForm={setForm}
        onClose={() => setDialogOpen(false)}
        onSave={save}
        saving={saving}
        branches={branches || []}
        regenerateNumber={regenerateNumber}
        jobLevels={jobLevels}
        therapyTypes={therapyTypes}
      />
      <DataPortabilityDialog
        open={portabilityOpen}
        onClose={() => setPortabilityOpen(false)}
        onCommitted={() => load()}
      />
    </div>
  );
}


// =================== Dialog ===================

function EmployeeDialog({ open, editing, form, update, updateNested, setForm, onClose, onSave, saving, branches, regenerateNumber, jobLevels, therapyTypes = [] }) {
  const [linkableSearch, setLinkableSearch] = useState("");
  const [linkableUsers, setLinkableUsers] = useState([]);
  const [linkableLoading, setLinkableLoading] = useState(false);
  // iter48 — Reports To picker
  const [rtSearch, setRtSearch] = useState("");
  const [rtOptions, setRtOptions] = useState([]);
  const [rtLoading, setRtLoading] = useState(false);

  useEffect(() => {
    if (!open) return;
    let cancelled = false;
    setRtLoading(true);
    const t = setTimeout(async () => {
      try {
        const params = { exclude_id: editing?.id || "" };
        if (rtSearch.trim()) params.q = rtSearch.trim();
        const { data } = await api.get("/workforce/employees/reports-to-options", { params });
        if (!cancelled) setRtOptions(data);
      } catch (e) {
        devWarn("[EmployeesPage] reports-to search failed:", e);
      }
      finally { if (!cancelled) setRtLoading(false); }
    }, 200);
    return () => { cancelled = true; clearTimeout(t); };
  }, [rtSearch, open, editing]);

  // Debounced fetch of linkable users when the search changes (only in create mode).
  useEffect(() => {
    if (!open || editing) return;
    let cancelled = false;
    setLinkableLoading(true);
    const t = setTimeout(async () => {
      try {
        const params = linkableSearch.trim() ? { q: linkableSearch.trim() } : {};
        const { data } = await api.get("/workforce/employees/linkable-users", { params });
        if (!cancelled) setLinkableUsers(data);
      } catch (e) {
        devWarn("[EmployeesPage] linkable-users search failed:", e);
      }
      finally { if (!cancelled) setLinkableLoading(false); }
    }, 250);
    return () => { cancelled = true; clearTimeout(t); };
  }, [linkableSearch, open, editing]);

  const linkToUser = (u) => {
    setForm(f => ({
      ...f,
      linked_user_id: u.id,
      name: u.name || f.name,
      email: u.email || f.email,
      phone: u.phone || f.phone,
      branch_id: (u.branch_ids && u.branch_ids[0]) || f.branch_id,
    }));
    toast.success(`Linked to ${u.name || u.email}`);
  };
  const unlink = () => setForm(f => ({ ...f, linked_user_id: null }));

  // iter-67e — Full inline "Enable Web Application Access" section.
  // Only used when editing an existing employee. On check, we expand a
  // section that mirrors the Settings → New User form (name, email,
  // phone, password, roles, branch). Unchecking discards. Saving
  // creates the user in the same call flow as Settings so the shared
  // user_limit counter is respected.
  const [webForm, setWebForm] = useState(null); // null = collapsed
  const [webFormError, setWebFormError] = useState("");
  const [planCap, setPlanCap] = useState({ users: 0, limit: null });
  useEffect(() => {
    // Load current plan usage once when the edit dialog opens for
    // an unlinked employee — we need it for the on-check cap gate.
    if (!open || !editing?.id || form.linked_user_id) return;
    (async () => {
      try {
        const { data } = await api.get("/plan");
        setPlanCap({
          users: data?.usage?.users || 0,
          limit: data?.plan?.user_limit ?? null,
        });
      } catch { /* silent — cap gate falls open when we can't read */ }
    })();
  }, [open, editing?.id, form.linked_user_id]);

  const atUserCap = (() => {
    const cap = planCap.limit;
    if (cap == null || cap === -1) return false;
    return planCap.users >= cap;
  })();

  const toggleWebAccess = async (checked) => {
    // State B branch — an existing linked user is being detached.
    if (form.linked_user_id) {
      if (checked) return; // no-op — already on
      const linkedName = editing?.linked_user_name || form.email;
      const confirmed = window.confirm(
        `Permanently delete the linked user account (${linkedName})? ` +
        `This uses the same delete flow as Settings → Users — the user ` +
        `will no longer be able to sign in and their app data (sessions, ` +
        `messages, approvals authored by them) is retained. The employee ` +
        `record itself is preserved.\n\nContinue?`,
      );
      if (!confirmed) return;
      try {
        await api.delete(`/users/${form.linked_user_id}`);
        // Backend cascades: employee.user_id + app_access_enabled cleared.
        setForm(f => ({
          ...f,
          linked_user_id: null,
          app_access_enabled: false,
        }));
        setWebForm(null);
        toast.success("Web application access removed. The employee record is preserved.");
      } catch (e) {
        toast.error(formatApiError(e, "Could not remove access."));
      }
      return;
    }
    // State A branch — no linked user yet.
    if (!checked) {
      setWebForm(null);
      setWebFormError("");
      setForm(f => ({ ...f, app_access_enabled: false }));
      return;
    }
    if (atUserCap) {
      toast.error(
        `Your plan allows up to ${planCap.limit} user${planCap.limit === 1 ? "" : "s"}. ` +
        `Upgrade in Settings → Plan or remove an inactive user first.`,
      );
      return; // do not check the box
    }
    // Prefill from the employee record. Fields stay editable.
    setWebForm({
      name: form.name || "",
      email: (form.email || "").toLowerCase(),
      phone: form.phone || "",
      password: "",
      role: form.is_therapist ? "therapist" : "reception",
      branch_ids: form.branch_id ? [form.branch_id] : [],
    });
    setWebFormError("");
    setForm(f => ({ ...f, app_access_enabled: true }));
  };

  const updateWebForm = (patch) => {
    setWebForm(w => ({ ...(w || {}), ...patch }));
    if (webFormError) setWebFormError("");
  };

  const validateWebForm = () => {
    if (!form.app_access_enabled) return true; // not enabled → nothing to validate
    if (!editing?.id) return true; // create mode never renders this section
    if (form.linked_user_id) return true; // State B — already linked
    if (!webForm) {
      setWebFormError("Please complete the web application access details before saving.");
      return false;
    }
    const { name, email, password, role } = webForm;
    if (!name?.trim() || !email?.trim() || !password?.trim() || !role) {
      setWebFormError("Please complete the web application access details before saving.");
      return false;
    }
    if (password.length < 6) {
      setWebFormError("Password must be at least 6 characters.");
      return false;
    }
    setWebFormError("");
    return true;
  };

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent
        className="w-[calc(100vw-1.5rem)] sm:w-full max-w-3xl max-h-[92vh] overflow-y-auto p-4 sm:p-6"
        data-testid="employee-dialog"
      >
        <DialogHeader>
          <DialogTitle>{editing ? `Edit ${editing.name || "employee"}` : "Add new employee"}</DialogTitle>
        </DialogHeader>

        <Tabs defaultValue="identity" className="mt-2">
          {/* Horizontal-scroll tab strip — dodges overlapping labels on
              small viewports. Active tab is emphasised with a filled
              primary background + white text so it's unmistakably
              selected on every screen size. */}
          <div className="relative -mx-4 sm:mx-0 border-b border-border">
            <TabsList
              className="w-max min-w-full h-auto p-1 gap-1 bg-muted/30 rounded-md flex overflow-x-auto scrollbar-thin whitespace-nowrap"
              style={{ scrollbarWidth: "thin", WebkitOverflowScrolling: "touch" }}
              data-testid="employee-tabs-list"
            >
              <TabsTrigger value="identity" data-testid="tab-identity"
                className="shrink-0 px-3 py-1.5 text-xs sm:text-sm rounded-md data-[state=active]:bg-primary data-[state=active]:text-primary-foreground data-[state=active]:shadow-sm data-[state=active]:font-semibold">
                Identity
              </TabsTrigger>
              <TabsTrigger value="employment" data-testid="tab-employment"
                className="shrink-0 px-3 py-1.5 text-xs sm:text-sm rounded-md data-[state=active]:bg-primary data-[state=active]:text-primary-foreground data-[state=active]:shadow-sm data-[state=active]:font-semibold">
                Employment
              </TabsTrigger>
              <TabsTrigger value="compensation" data-testid="tab-compensation"
                className="shrink-0 px-3 py-1.5 text-xs sm:text-sm rounded-md data-[state=active]:bg-primary data-[state=active]:text-primary-foreground data-[state=active]:shadow-sm data-[state=active]:font-semibold">
                Compensation
              </TabsTrigger>
              <TabsTrigger value="bank_statutory" data-testid="tab-bank"
                className="shrink-0 px-3 py-1.5 text-xs sm:text-sm rounded-md data-[state=active]:bg-primary data-[state=active]:text-primary-foreground data-[state=active]:shadow-sm data-[state=active]:font-semibold">
                Bank &amp; Statutory
              </TabsTrigger>
              <TabsTrigger value="emergency" data-testid="tab-emergency"
                className="shrink-0 px-3 py-1.5 text-xs sm:text-sm rounded-md data-[state=active]:bg-primary data-[state=active]:text-primary-foreground data-[state=active]:shadow-sm data-[state=active]:font-semibold">
                Emergency &amp; Skills
              </TabsTrigger>
              <TabsTrigger value="certs" data-testid="tab-certs"
                className="shrink-0 px-3 py-1.5 text-xs sm:text-sm rounded-md data-[state=active]:bg-primary data-[state=active]:text-primary-foreground data-[state=active]:shadow-sm data-[state=active]:font-semibold">
                Certifications
              </TabsTrigger>
              {editing && (
                <TabsTrigger value="balance" data-testid="tab-balance"
                  className="shrink-0 px-3 py-1.5 text-xs sm:text-sm rounded-md data-[state=active]:bg-primary data-[state=active]:text-primary-foreground data-[state=active]:shadow-sm data-[state=active]:font-semibold">
                  Leave Balance
                </TabsTrigger>
              )}
            </TabsList>
          </div>

          {/* ---- Identity + optional link-to-user ---- */}
          <TabsContent value="identity" className="space-y-4 pt-4">
            {!editing && (
              <div className="rounded-md border bg-muted/30 p-3 space-y-2">
                <div className="flex items-center gap-2 text-sm font-medium">
                  <LinkIcon size={14} /> Link to existing user account (optional)
                </div>
                <div className="text-xs text-muted-foreground">
                  Non-Parent/non-Guardian workspace users appear here. Selecting one auto-fills name, phone and branch.
                </div>
                {form.linked_user_id ? (
                  <div className="flex items-center gap-2 text-sm bg-emerald-50 border border-emerald-200 rounded p-2">
                    <UserCircle size={16} className="text-emerald-700" />
                    <span className="text-emerald-900">Linked: <b>{form.name}</b> ({form.email})</span>
                    <Button variant="ghost" size="sm" onClick={unlink} className="ml-auto h-6 text-xs" data-testid="employee-unlink">Unlink</Button>
                  </div>
                ) : (
                  <>
                    <Input
                      placeholder="Search users by name or email…"
                      value={linkableSearch}
                      onChange={e => setLinkableSearch(e.target.value)}
                      className="h-8"
                      data-testid="linkable-search"
                    />
                    <div className="max-h-40 overflow-y-auto space-y-1">
                      {linkableLoading && <div className="text-xs text-muted-foreground p-2">Searching…</div>}
                      {!linkableLoading && linkableUsers.length === 0 && (
                        <div className="text-xs text-muted-foreground p-2">
                          No linkable users found. Create the employee record with name/email below — you can link later once the user is added to the workspace.
                        </div>
                      )}
                      {linkableUsers.slice(0, 8).map(u => (
                        <button
                          key={u.id} type="button"
                          onClick={() => linkToUser(u)}
                          data-testid={`linkable-${u.id}`}
                          className="w-full flex items-center gap-2 text-left rounded px-2 py-1.5 hover:bg-background text-sm"
                        >
                          <UserCircle size={16} className="text-muted-foreground" />
                          <div className="flex-1">
                            <div className="font-medium">{u.name}</div>
                            <div className="text-[11px] text-muted-foreground">{u.email} · {(u.roles || []).join(", ")}</div>
                          </div>
                        </button>
                      ))}
                    </div>
                  </>
                )}
              </div>
            )}
            <div className="grid grid-cols-2 gap-3">
              <FormRow label="Full name *"><Input value={form.name} onChange={e => update("name", e.target.value)} data-testid="employee-name" /></FormRow>
              <FormRow label={
                <span className="flex items-center gap-1.5">
                  Employee #
                  {!editing && form.__auto_generate === false && <span className="text-rose-600">*</span>}
                  {(!editing && form.__auto_generate !== false) && (
                    <span className="text-[10px] text-emerald-700 font-normal">(auto-suggested)</span>
                  )}
                </span>
              }>
                <div className="flex gap-1.5">
                  <Input
                    value={form.employee_number || ""}
                    onChange={e => update("employee_number", e.target.value)}
                    placeholder={form.__auto_generate === false ? "e.g. EMP-001" : "auto-generated"}
                    className="font-mono"
                    data-testid="employee-number-input"
                  />
                  {regenerateNumber && (
                    <Button
                      type="button"
                      variant="outline"
                      size="sm"
                      onClick={regenerateNumber}
                      className="whitespace-nowrap"
                      title="Regenerate a new number based on the current settings"
                      data-testid="employee-number-regenerate"
                    >
                      Auto-gen
                    </Button>
                  )}
                </div>
              </FormRow>
              <FormRow label="Status">
                <Select value={form.status} onValueChange={v => update("status", v)}>
                  <SelectTrigger data-testid="employee-status"><SelectValue /></SelectTrigger>
                  <SelectContent>{STATUS_OPTIONS.map(s => (<SelectItem key={s.value} value={s.value}>{s.label}</SelectItem>))}</SelectContent>
                </Select>
              </FormRow>
              <FormRow label="Email">
                <Input type="email" value={form.email || ""} onChange={e => update("email", e.target.value)} disabled={!!form.linked_user_id} data-testid="employee-email" />
              </FormRow>
              <FormRow label="Phone"><Input value={form.phone || ""} onChange={e => update("phone", e.target.value)} data-testid="employee-phone" /></FormRow>
            </div>

            {/* iter-67e — Web Application Access section.
                On the *Add* form the checkbox is always disabled;
                on *Edit* the checkbox drives State A (create user
                inline) or State B (delete linked user via Settings
                delete flow). */}
            <div className="rounded-md border p-3 bg-muted/20 space-y-2" data-testid="web-access-section">
              <label className={`flex items-center gap-2 ${editing ? "cursor-pointer" : "cursor-not-allowed opacity-70"}`}>
                <Checkbox
                  checked={form.linked_user_id ? true : !!form.app_access_enabled}
                  disabled={!editing}
                  onCheckedChange={editing ? toggleWebAccess : undefined}
                  data-testid="employee-app-access"
                />
                <span className="text-sm font-medium">Enable Web Application Access</span>
              </label>
              {!editing && (
                <div className="text-[11px] text-muted-foreground pl-6">
                  Web application access can be enabled after the employee record is saved.
                </div>
              )}

              {/* State B — already linked. Read-only summary + Manage User button. */}
              {editing && form.linked_user_id && (
                <div
                  className="pl-6 grid gap-2 sm:grid-cols-2 text-[12px] rounded-md border bg-white p-3"
                  data-testid="web-access-linked-summary"
                >
                  <div>
                    <div className="text-[10px] uppercase tracking-wide text-muted-foreground">Name</div>
                    <div className="font-medium">{editing.linked_user_name || form.name || "—"}</div>
                  </div>
                  <div>
                    <div className="text-[10px] uppercase tracking-wide text-muted-foreground">Email</div>
                    <div className="font-mono text-[11px]">{editing.linked_user_email || form.email || "—"}</div>
                  </div>
                  <div>
                    <div className="text-[10px] uppercase tracking-wide text-muted-foreground">Role</div>
                    <div>{(editing.linked_user_roles || []).join(", ") || "—"}</div>
                  </div>
                  <div className="flex items-end justify-end">
                    <Button
                      type="button"
                      size="sm"
                      variant="outline"
                      className="h-7 text-[11px]"
                      onClick={() => {
                        // Navigate to Settings → Users tab. The tab
                        // reads `?highlight={user_id}` to scroll to
                        // and open that user's row (existing behavior).
                        window.location.assign(`/settings?tab=users&highlight=${form.linked_user_id}`);
                      }}
                      data-testid="manage-user-btn"
                    >
                      Manage User
                    </Button>
                  </div>
                </div>
              )}

              {/* State A — checkbox checked, no linked user yet.
                  Inline user form mirrors Settings → New User. */}
              {editing && !form.linked_user_id && form.app_access_enabled && webForm && (
                <div
                  className="pl-6 space-y-2 rounded-md border bg-white p-3"
                  data-testid="web-access-inline-form"
                >
                  <div className="text-[11px] text-muted-foreground">
                    Same fields as <em>Settings → New User</em>. Prefilled from this employee.
                    All fields are editable. The user counts against your plan&apos;s user limit
                    (currently {planCap.users}{planCap.limit == null || planCap.limit === -1 ? "" : ` / ${planCap.limit}`}).
                  </div>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                    <div>
                      <Label className="text-[10px] uppercase tracking-wide">Full name *</Label>
                      <Input
                        value={webForm.name}
                        onChange={e => updateWebForm({ name: e.target.value })}
                        className="h-8 text-xs"
                        data-testid="web-access-name"
                      />
                    </div>
                    <div>
                      <Label className="text-[10px] uppercase tracking-wide">Email *</Label>
                      <Input
                        type="email"
                        value={webForm.email}
                        onChange={e => updateWebForm({ email: e.target.value.toLowerCase() })}
                        className="h-8 text-xs"
                        data-testid="web-access-email"
                      />
                    </div>
                    <div>
                      <Label className="text-[10px] uppercase tracking-wide">Phone</Label>
                      <Input
                        value={webForm.phone}
                        onChange={e => updateWebForm({ phone: e.target.value })}
                        className="h-8 text-xs"
                        data-testid="web-access-phone"
                      />
                    </div>
                    <div>
                      <Label className="text-[10px] uppercase tracking-wide">Temporary password *</Label>
                      <Input
                        type="text"
                        value={webForm.password}
                        onChange={e => updateWebForm({ password: e.target.value })}
                        className="h-8 text-xs font-mono"
                        autoComplete="new-password"
                        placeholder="Share with the employee"
                        data-testid="web-access-password"
                      />
                    </div>
                    <div>
                      <Label className="text-[10px] uppercase tracking-wide">Primary role *</Label>
                      <Select
                        value={webForm.role}
                        onValueChange={v => updateWebForm({ role: v })}
                      >
                        <SelectTrigger className="h-8 text-xs" data-testid="web-access-role"><SelectValue /></SelectTrigger>
                        <SelectContent>
                          <SelectItem value="therapist">Therapist</SelectItem>
                          <SelectItem value="reception">Reception</SelectItem>
                          <SelectItem value="accountant">Accountant</SelectItem>
                          <SelectItem value="admin">Admin</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                    <div>
                      <Label className="text-[10px] uppercase tracking-wide">Branch</Label>
                      <Select
                        value={(webForm.branch_ids || [])[0] || ""}
                        onValueChange={v => updateWebForm({ branch_ids: v ? [v] : [] })}
                      >
                        <SelectTrigger className="h-8 text-xs" data-testid="web-access-branch"><SelectValue placeholder="All branches" /></SelectTrigger>
                        <SelectContent>
                          {(branches || []).map(b => (
                            <SelectItem key={b.id} value={b.id}>{b.name}</SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                  </div>
                  {webFormError && (
                    <div className="text-[11px] text-red-600" data-testid="web-access-error">
                      {webFormError}
                    </div>
                  )}
                </div>
              )}
            </div>
          </TabsContent>

          {/* ---- Employment profile ---- */}
          <TabsContent value="employment" className="space-y-3 pt-4">
            <div className="grid grid-cols-2 gap-3">
              <FormRow label="Designation"><Input value={form.designation || ""} onChange={e => update("designation", e.target.value)} data-testid="employee-designation" /></FormRow>
              <FormRow label="Department"><Input value={form.department || ""} onChange={e => update("department", e.target.value)} data-testid="employee-department" /></FormRow>
              <FormRow label="Branch">
                <Select value={form.branch_id || ""} onValueChange={v => update("branch_id", v)}>
                  <SelectTrigger data-testid="employee-branch"><SelectValue placeholder="Select branch" /></SelectTrigger>
                  <SelectContent>{branches.map(b => (<SelectItem key={b.id} value={b.id}>{b.name}</SelectItem>))}</SelectContent>
                </Select>
              </FormRow>
              <FormRow label="Employment type">
                <Select value={form.employment_type} onValueChange={v => update("employment_type", v)}>
                  <SelectTrigger data-testid="employee-type"><SelectValue /></SelectTrigger>
                  <SelectContent>{EMPLOYMENT_TYPES.map(t => (<SelectItem key={t.value} value={t.value}>{t.label}</SelectItem>))}</SelectContent>
                </Select>
              </FormRow>
              <FormRow label="Joined on"><Input type="date" value={form.joined_on || ""} onChange={e => update("joined_on", e.target.value)} data-testid="employee-joined" /></FormRow>
            </div>

            {/* iter48 — Job Level + Reports To */}
            <div className="rounded-md border p-3 bg-muted/20 space-y-3">
              <div className="text-sm font-medium flex items-center gap-2">
                <span>Workflow & Reporting</span>
                <Badge variant="outline" className="text-[9px]">iter48</Badge>
              </div>
              <div className="grid grid-cols-2 gap-3">
                {jobLevels && jobLevels.length > 0 && (
                  <FormRow label="Job Level">
                    <Select value={form.job_level_id || ""} onValueChange={v => update("job_level_id", v === "__none" ? "" : v)}>
                      <SelectTrigger data-testid="employee-job-level"><SelectValue placeholder="Select level" /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="__none">— None —</SelectItem>
                        {[...jobLevels].sort((a, b) => (a.rank || 0) - (b.rank || 0)).map(l => (
                          <SelectItem key={l.id} value={l.id}>{l.name} (rank {l.rank})</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </FormRow>
                )}
                <FormRow label={
                  <span className="flex items-center gap-1.5">
                    Reports To {!form.is_owner && <span className="text-rose-600">*</span>}
                    <span className="text-[10px] text-muted-foreground font-normal">(line manager)</span>
                  </span>
                }>
                  {form.is_owner ? (
                    <div className="text-[11px] text-muted-foreground p-2 border rounded bg-white">
                      Owner is exempt from Reports To.
                    </div>
                  ) : (
                    <>
                      {form.reports_to && (
                        <div className="flex items-center gap-2 text-xs bg-primary/5 border border-primary/20 rounded p-1.5 mb-1.5" data-testid="reports-to-selected">
                          <UserCircle size={13} className="text-primary" />
                          <span className="flex-1">
                            {(rtOptions.find(o => o.id === form.reports_to) || {}).employee_number || ""} —
                            {" "}{(rtOptions.find(o => o.id === form.reports_to) || {}).name || (editing?.reports_to === form.reports_to ? "current manager" : "selected")}
                          </span>
                          <Button variant="ghost" size="sm" onClick={() => update("reports_to", "")} className="h-6 text-[10px]" data-testid="reports-to-clear">Clear</Button>
                        </div>
                      )}
                      <Input
                        placeholder="Search by employee # or name…"
                        value={rtSearch}
                        onChange={e => setRtSearch(e.target.value)}
                        className="h-8"
                        data-testid="reports-to-search"
                      />
                      {(rtLoading || rtOptions.length > 0) && (
                        <div className="max-h-32 overflow-y-auto border rounded mt-1 bg-white">
                          {rtLoading && <div className="text-[10px] text-muted-foreground p-2">Searching…</div>}
                          {rtOptions.slice(0, 8).map(o => (
                            <button
                              key={o.id} type="button"
                              onClick={() => { update("reports_to", o.id); setRtSearch(""); }}
                              className="w-full text-left px-2 py-1 hover:bg-primary/5 text-xs flex items-center gap-2"
                              data-testid={`reports-to-option-${o.id}`}
                            >
                              <span className="font-mono text-primary">{o.employee_number || "—"}</span>
                              <span>{o.name}</span>
                              {o.designation && <span className="text-muted-foreground">· {o.designation}</span>}
                            </button>
                          ))}
                        </div>
                      )}
                    </>
                  )}
                </FormRow>
                <FormRow label="Owner exemption">
                  <label className="flex items-center gap-2 h-9 text-xs cursor-pointer">
                    <Checkbox
                      checked={!!form.is_owner}
                      onCheckedChange={v => update("is_owner", !!v)}
                      data-testid="employee-is-owner"
                    />
                    Mark as Owner (exempt from Reports To)
                  </label>
                </FormRow>
              </div>
            </div>

            {/* iter-67 — Therapies section. Sits directly below
                Workflow & Reporting on the Employment tab. Checkbox A
                is the therapist gate; the therapies multi-select
                only appears when the checkbox is on. Storing
                `therapies=[]` when the checkbox flips off is
                enforced both by the local effect below and by the
                backend as a safety net. */}
            <div className="rounded-md border p-3 bg-muted/20 space-y-3" data-testid="employee-therapies-section">
              <div className="text-sm font-medium flex items-center gap-2">
                <span>Therapies</span>
                <Badge variant="outline" className="text-[9px]">iter67</Badge>
              </div>
              <label className="flex items-center gap-2 cursor-pointer" data-testid="employee-is-therapist-label">
                <Checkbox
                  checked={!!form.is_therapist}
                  onCheckedChange={v => {
                    const on = !!v;
                    setForm(f => ({
                      ...f,
                      is_therapist: on,
                      // Clear the therapies array when the gate is
                      // turned off — matches the backend invariant.
                      therapies: on ? (f.therapies || []) : [],
                    }));
                  }}
                  data-testid="employee-is-therapist"
                />
                <span className="text-sm">Is a Therapist</span>
              </label>
              {form.is_therapist && !form.linked_user_id && (
                <div
                  className="rounded-md border border-amber-400 bg-amber-50 text-amber-900 text-[11px] p-2"
                  data-testid="therapist-needs-user-warning"
                >
                  <strong>This employee has no app login.</strong> Therapists
                  must be linked to an app user or they will not appear in
                  the Student Queue for session assignment. Tick{" "}
                  <em>Enable Web Application Access</em> on the Identity tab
                  to create their login now.
                </div>
              )}
              {form.is_therapist && form.linked_user_id && (
                <div className="pt-1">
                  <div className="text-[11px] text-muted-foreground mb-2">
                    Pick the therapy types this therapist can handle. Leave every
                    checkbox unticked to appear as an option for <em>every</em>{" "}
                    therapy type.
                  </div>
                  {therapyTypes.length === 0 ? (
                    <div className="text-[11px] text-muted-foreground italic">
                      No therapy types configured yet. Add them in Settings → Therapy Types.
                    </div>
                  ) : (
                    <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-2">
                      {therapyTypes.filter(t => t.active !== false).map(t => {
                        const checked = (form.therapies || []).includes(t.id);
                        return (
                          <label
                            key={t.id}
                            className={`flex items-center gap-2 p-2 border rounded-md cursor-pointer transition-colors ${
                              checked ? "border-primary/40 bg-primary/[0.04]" : "hover:bg-muted/40"
                            }`}
                          >
                            <Checkbox
                              checked={checked}
                              onCheckedChange={() => {
                                setForm(f => {
                                  const cur = new Set(f.therapies || []);
                                  cur.has(t.id) ? cur.delete(t.id) : cur.add(t.id);
                                  return { ...f, therapies: Array.from(cur) };
                                });
                              }}
                              data-testid={`employee-therapy-${t.id}`}
                            />
                            <span
                              className="inline-flex h-4 shrink-0 items-center justify-center rounded text-white text-[9px] font-bold px-1"
                              style={{ backgroundColor: t.color || "#4A6753" }}
                              title={t.code}
                            >
                              {t.code || "—"}
                            </span>
                            <span className="text-sm truncate">{t.name}</span>
                          </label>
                        );
                      })}
                    </div>
                  )}
                </div>
              )}
            </div>

            <NestedListEditor
              title="Employment history"
              items={form.employment_history || []}
              onChange={items => update("employment_history", items)}
              empty={{ role: "", branch_id: "", department: "", start_date: "", end_date: "", notes: "" }}
              fields={[
                { key: "role", label: "Role" },
                { key: "department", label: "Department" },
                { key: "start_date", label: "Start", type: "date" },
                { key: "end_date", label: "End", type: "date" },
              ]}
              addLabel="Add previous role"
              testId="history"
            />
          </TabsContent>

          {/* ---- Compensation (Phase 4-b) ---- */}
          <TabsContent value="compensation" className="space-y-3 pt-4">
            <div className="text-xs text-muted-foreground">
              Compensation lives exclusively on the Employee record. Role changes never wipe these lines.
              Deduction rows are subtracted from the net salary readout.
            </div>
            <CompensationLineItems
              model={form.compensation_model}
              setModel={v => update("compensation_model", v)}
              lines={form.compensation_lines || []}
              setLines={arr => update("compensation_lines", arr)}
              showTemplates={true}
              disabled={false}
              dataTestidPrefix="employee-comp"
            />
          </TabsContent>

          {/* ---- Bank + Statutory ---- */}
          <TabsContent value="bank_statutory" className="space-y-4 pt-4">
            <div>
              <div className="text-sm font-medium mb-2">Bank details</div>
              <div className="grid grid-cols-2 gap-3">
                <FormRow label="Account holder"><Input value={form.bank_details?.account_holder || ""} onChange={e => updateNested("bank_details", "account_holder", e.target.value)} /></FormRow>
                <FormRow label="Bank name"><Input value={form.bank_details?.bank_name || ""} onChange={e => updateNested("bank_details", "bank_name", e.target.value)} /></FormRow>
                <FormRow label="Account number"><Input value={form.bank_details?.account_number || ""} onChange={e => updateNested("bank_details", "account_number", e.target.value)} data-testid="employee-account-no" /></FormRow>
                <FormRow label="IFSC"><Input value={form.bank_details?.ifsc || ""} onChange={e => updateNested("bank_details", "ifsc", e.target.value.toUpperCase())} data-testid="employee-ifsc" /></FormRow>
              </div>
            </div>
            <div>
              <div className="text-sm font-medium mb-2">Statutory (India)</div>
              <div className="grid grid-cols-2 gap-3">
                <FormRow label="PAN"><Input value={form.statutory?.pan || ""} onChange={e => updateNested("statutory", "pan", e.target.value.toUpperCase())} data-testid="employee-pan" /></FormRow>
                <FormRow label="Aadhaar (last 4)"><Input maxLength={4} value={form.statutory?.aadhaar || ""} onChange={e => updateNested("statutory", "aadhaar", e.target.value)} /></FormRow>
                <FormRow label="PF UAN"><Input value={form.statutory?.pf_uan || ""} onChange={e => updateNested("statutory", "pf_uan", e.target.value)} /></FormRow>
                <FormRow label="PF number"><Input value={form.statutory?.pf_number || ""} onChange={e => updateNested("statutory", "pf_number", e.target.value)} /></FormRow>
                <FormRow label="ESI number"><Input value={form.statutory?.esi_number || ""} onChange={e => updateNested("statutory", "esi_number", e.target.value)} /></FormRow>
              </div>
            </div>
          </TabsContent>

          {/* ---- Emergency + Skills ---- */}
          <TabsContent value="emergency" className="space-y-4 pt-4">
            <NestedListEditor
              title="Emergency contacts"
              items={form.emergency_contacts || []}
              onChange={items => update("emergency_contacts", items)}
              empty={{ name: "", relation: "", phone: "", email: "" }}
              fields={[
                { key: "name", label: "Name" },
                { key: "relation", label: "Relation" },
                { key: "phone", label: "Phone" },
                { key: "email", label: "Email" },
              ]}
              addLabel="Add contact"
              testId="emergency"
            />
            <NestedListEditor
              title="Skills"
              items={form.skills || []}
              onChange={items => update("skills", items)}
              empty={{ name: "", proficiency: "intermediate", years: 0 }}
              fields={[
                { key: "name", label: "Skill" },
                { key: "proficiency", label: "Level" },
                { key: "years", label: "Years", type: "number" },
              ]}
              addLabel="Add skill"
              testId="skill"
            />
          </TabsContent>

          {/* ---- Certifications ---- */}
          <TabsContent value="certs" className="space-y-3 pt-4">
            <NestedListEditor
              title="Certifications"
              items={form.certifications || []}
              onChange={items => update("certifications", items)}
              empty={{ name: "", issuing_body: "", reference_no: "", issued_on: "", expires_on: "" }}
              fields={[
                { key: "name", label: "Certification" },
                { key: "issuing_body", label: "Issuing body" },
                { key: "issued_on", label: "Issued", type: "date" },
                { key: "expires_on", label: "Expires", type: "date", warn: true },
              ]}
              addLabel="Add certification"
              testId="cert"
            />
            <p className="text-[11px] text-muted-foreground">
              Certifications expiring in the next 90 days appear on the
              dashboard alert card. Set an <i>Expires</i> date to opt in.
            </p>
          </TabsContent>

          {editing && (
            <TabsContent value="balance" className="space-y-3 pt-4">
              <LeaveBalanceTracker employeeId={editing.id} />
            </TabsContent>
          )}
        </Tabs>

        <DialogFooter className="pt-4 border-t">
          <Button variant="outline" onClick={onClose} disabled={saving}>Cancel</Button>
          <Button
            onClick={async () => {
              // iter-67e — Gate the save so the embedded user form
              // is validated first when Web App Access is being
              // enabled in this session.
              if (!validateWebForm()) return;
              // Persist the employee edits first, then if we're in
              // State A create the user via the /create-login
              // endpoint (which uses the shared user_limit counter).
              await onSave({
                webFormPayload: (editing && form.app_access_enabled && !form.linked_user_id && webForm)
                  ? {
                      name: webForm.name.trim(),
                      email: webForm.email.trim().toLowerCase(),
                      phone: webForm.phone?.trim() || null,
                      password: webForm.password,
                      roles: [webForm.role],
                      branch_ids: webForm.branch_ids || [],
                    }
                  : null,
              });
            }}
            disabled={saving}
            data-testid="employee-save"
          >
            {saving ? "Saving…" : (editing ? "Save changes" : "Add employee")}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}


// =================== Small helpers ===================

function FormRow({ label, children }) {
  return (
    <div className="space-y-1">
      <Label className="text-xs">{label}</Label>
      {children}
    </div>
  );
}

function NestedListEditor({ title, items, onChange, empty, fields, addLabel, testId }) {
  const setAt = (i, key, val) => {
    const next = [...items];
    next[i] = { ...next[i], [key]: val };
    onChange(next);
  };
  const remove = (i) => {
    const next = [...items];
    next.splice(i, 1);
    onChange(next);
  };
  // Stable-key generation for new rows so deleting an entry doesn't
  // remount later rows and steal their focus. `id` may already exist
  // for saved certifications; new rows get `_ui_key` which we strip
  // before POSTing to the backend (see `save` in EmployeesPage).
  const add = () => onChange([
    ...items,
    { ...empty, _ui_key: (typeof crypto !== "undefined" && crypto.randomUUID) ? crypto.randomUUID() : `k-${Date.now()}-${Math.random()}` },
  ]);
  return (
    <div className="space-y-2">
      <div className="flex items-center justify-between">
        <div className="text-sm font-medium">{title}</div>
        <Button variant="outline" size="sm" onClick={add} className="h-7 gap-1" data-testid={`${testId}-add`}><Plus size={11} /> {addLabel}</Button>
      </div>
      {items.length === 0 && <div className="text-xs text-muted-foreground italic">None yet.</div>}
      {items.map((row, i) => (
        <div key={row.id || row._ui_key || `${testId}-${i}`} className="grid grid-cols-2 md:grid-cols-4 gap-2 items-end border rounded p-2 bg-muted/20">
          {fields.map(f => {
            const val = row[f.key] || "";
            const warnExpired = f.warn && val && val < new Date().toISOString().slice(0, 10);
            return (
              <div key={f.key} className="space-y-1">
                <Label className="text-[11px] text-muted-foreground flex items-center gap-1">
                  {f.label}
                  {warnExpired && <WarningCircle size={11} className="text-rose-600" title="Expired" />}
                </Label>
                <Input
                  type={f.type || "text"}
                  value={val}
                  onChange={e => setAt(i, f.key, f.type === "number" ? parseFloat(e.target.value || 0) : e.target.value)}
                  className="h-8"
                  data-testid={`${testId}-${i}-${f.key}`}
                />
              </div>
            );
          })}
          <Button variant="ghost" size="sm" onClick={() => remove(i)} className="text-rose-600 hover:text-rose-700 h-8" data-testid={`${testId}-${i}-remove`}>
            <Trash size={13} />
          </Button>
        </div>
      ))}
    </div>
  );
}


// =================== Leave Balance Tracker (Phase 3c add-on) ===================

function LeaveBalanceTracker({ employeeId }) {
  const [data, setData] = useState(null);
  const [year, setYear] = useState(new Date().getFullYear());
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState(null);

  useEffect(() => {
    let cancelled = false;
    const load = async () => {
      setLoading(true);
      setErr(null);
      try {
        const { data } = await api.get("/leaves/balance/detail", {
          params: { employee_id: employeeId, year },
        });
        if (!cancelled) setData(data);
      } catch (e) {
        if (!cancelled) setErr(e?.response?.data?.detail || e.message);
      } finally {
        if (!cancelled) setLoading(false);
      }
    };
    load();
    return () => { cancelled = true; };
  }, [employeeId, year]);

  if (loading) return <div className="p-4 text-sm text-muted-foreground text-center">Loading balances…</div>;
  if (err) return <div className="p-3 rounded border border-destructive/40 bg-destructive/5 text-sm text-destructive">{err}</div>;
  if (!data) return null;

  return (
    <div className="space-y-3" data-testid="leave-balance-tracker">
      <div className="flex items-center justify-between">
        <div className="text-xs text-muted-foreground">
          Year <input
            type="number"
            value={year}
            onChange={e => setYear(parseInt(e.target.value) || year)}
            className="w-20 border rounded px-2 py-0.5 text-xs ml-1"
            data-testid="leave-balance-year"
          />
        </div>
        {!data.addon_active && (
          <span className="text-[11px] px-2 py-0.5 rounded-full bg-amber-100 text-amber-800">
            Carry-forward & encashment disabled (add-on inactive)
          </span>
        )}
        {data.total_encashable > 0 && (
          <span className="text-[11px] px-2 py-0.5 rounded-full bg-emerald-100 text-emerald-800" data-testid="total-encashable-badge">
            Encashable: {data.total_encashable} days
          </span>
        )}
      </div>

      {data.balances.length === 0 ? (
        <div className="p-6 text-center text-sm text-muted-foreground border rounded">
          No leave types configured. Add them in Settings → Attendance & Leave.
        </div>
      ) : (
        <div className="border rounded overflow-hidden">
          <table className="w-full text-sm">
            <thead className="bg-muted/50 text-xs uppercase tracking-wide text-muted-foreground">
              <tr>
                <th className="text-left px-3 py-2">Leave Type</th>
                <th className="text-right px-2 py-2">Entitled</th>
                <th className="text-right px-2 py-2">Carry Fwd</th>
                <th className="text-right px-2 py-2">Opening</th>
                <th className="text-right px-2 py-2">Used</th>
                <th className="text-right px-2 py-2">Pending</th>
                <th className="text-right px-2 py-2 font-semibold">Remaining</th>
                <th className="text-right px-3 py-2">Encashable</th>
              </tr>
            </thead>
            <tbody className="divide-y">
              {data.balances.map(b => (
                <tr key={b.leave_type_id} data-testid={`balance-row-${b.leave_type_code}`}>
                  <td className="px-3 py-2">
                    <div className="flex items-center gap-2">
                      {b.color && (
                        <span
                          className="w-2 h-2 rounded-full inline-block"
                          style={{ background: b.color }}
                        />
                      )}
                      <span className="font-medium">{b.leave_type_name}</span>
                      {!b.is_paid && <span className="text-[9px] px-1 rounded bg-slate-100 text-slate-600">Unpaid</span>}
                    </div>
                    <div className="text-[10px] text-muted-foreground mt-0.5 flex gap-1">
                      {b.carry_forward_enabled && <span className="px-1 rounded bg-indigo-50 text-indigo-700">CF</span>}
                      {b.encashment_enabled    && <span className="px-1 rounded bg-emerald-50 text-emerald-700">Encash</span>}
                    </div>
                  </td>
                  <td className="text-right px-2 py-2">{b.entitled}</td>
                  <td className="text-right px-2 py-2 text-indigo-700">{b.carry_forward || "—"}</td>
                  <td className="text-right px-2 py-2 font-medium">{b.opening}</td>
                  <td className="text-right px-2 py-2 text-rose-600">{b.approved}</td>
                  <td className="text-right px-2 py-2 text-amber-600">{b.pending || "—"}</td>
                  <td className="text-right px-2 py-2 font-semibold text-emerald-700">{b.remaining}</td>
                  <td className="text-right px-3 py-2 text-emerald-600">{b.encashable || "—"}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
      <p className="text-[11px] text-muted-foreground">
        Encashment values are calculated end-of-year on unused paid leave when the
        <b> Encashment</b> flag is on for that leave type. Carry-forward carries any
        unused entitlement from last year into this year&apos;s opening balance.
      </p>
    </div>
  );
}
